/**
 * WISE Web Service - Product 95: COMMERCIO_PERSONA_FISICA
 * Client-Side Validation Rules
 *
 * This module contains comprehensive validation rules for Product 95 (Commercial Person Physical)
 * including guarantee compatibility, parameters, capital limits, and business logic validations.
 */

// ============================================================================
// 1. GUARANTEE COMPATIBILITY MATRIX
// ============================================================================

/**
 * Mutually Exclusive Guarantees
 * Key: Guarantee code
 * Value: Array of guarantee codes that cannot coexist
 */
export const INCOMPATIBLE_GUARANTEES = {
  'CONT.F': ['CONT-8', 'FABB-9', 'FABB-8'],
  'CONT-8': ['CONT.F', 'CONT-8'], // Cannot duplicate same type
  'FABB-9': ['CONT.F', 'FABB-8'],
  'FABB-8': ['FABB-9', 'CONT.F'],
  FABB: ['RILO'], // Rule 03365: FABB excludes RILO
  RILO: ['RTER-8', 'RTER-9', 'FABB'], // Rule 03365: RILO excludes FABB
  'RTER-8': ['RILO'],
  'RTER-9': ['RILO'],
  'REFR-8': ['REFR-9'],
  'REFR-9': ['REFR-8'],
  RCT: ['PROP'], // Rule 03365: RCT excludes PROP
  PROP: ['RCT'], // Rule 03365: PROP excludes RCT
};

/**
 * Required Guarantee Combinations
 * Key: Guarantee code
 * Value: Array of guarantee codes that are required when key is present
 */
export const REQUIRED_GUARANTEE_COMBINATIONS = {
  'CONT-8': ['RCT'],
  'FABB-9': ['CONT-8', 'RCT'],
  'FABB-8': ['RCT'],
  'CONT.F': [['FABB', 'RILO'], 'RCT', 'CONT'],
  RILO: ['CONT', 'RCT'],
  'RTER-8': ['RCT'],
  'RTER-9': ['RCT'],
  'REFR-8': ['RCT'],
  'REFR-9': ['RCT'],
  CRIS: [['FABB', 'RILO'], 'CONT', 'RCT'],
  INCI: ['RCT'],
  REFR: ['FABB', 'CONT', 'RCT'],
  RTER: ['FABB', 'CONT', 'RCT'],
  ELE: ['CONT'],
  // Any property guarantee requires RCT
};

// ============================================================================
// 2. PRODUCT PARAMETER VALIDATION RULES
// ============================================================================

/**
 * Parameter rules for each guarantee type
 * Structure: { guarantee_code: { progressivo_parametro: { required_value, description } } }
 */
export const GUARANTEE_PARAMETERS = {
  'CONT.F': {
    1: { required_value: 'D', description: 'Categoria Rischio D' },
    4: { required_value: '1', description: 'Parametro 4 valore 1' },
    5: { required_value: '1', description: 'Parametro 5 valore 1' },
    6: { required_value: '2', description: 'Parametro 6 valore 2' },
  },
  'CONT-8': {
    '1_asset1': { required_value: 'C', description: 'Categoria Rischio C (Asset 1)', asset: 1 },
    '2_asset1': { required_value: '2', description: 'Condizioni aggiuntive', asset: 1 },
    '1_asset2_3': { required_value: 'B', description: 'Categoria Rischio B (Assets 2-3)', asset: [2, 3] },
    '2_asset2_3': { required_value: '2', description: 'Condizioni aggiuntive', asset: [2, 3] },
  },
  'FABB-9': {
    1: { required_value: 'B', description: 'Categoria Rischio B' },
    2: { required_value: '1', description: 'Parametro 2 valore 1' },
    3: { required_value: '2', description: 'Parametro 3 valore 2' },
  },
  'FABB-8': {
    1: { required_value: 'B', description: 'Categoria Rischio B' },
    2: { required_value: '1', description: 'Parametro 2 valore 1' },
  },
  RCT: {
    1: { required_value: '1', description: 'Parametro RCT 1' },
    2: { required_value: 'A', description: 'Parametro RCT 2' },
    n_addetti: {
      required: true, min: 1, max: 100, description: 'Numero addetti obbligatorio (1-100)',
    },
  },
};

// ============================================================================
// 3. CAPITAL AMOUNT VALIDATION TABLE
// ============================================================================

export const CAPITAL_LIMITS = {
  'CONT.F': {
    min: 5000, max: 50000, recommended_min: 5000, recommended_max: 50000,
  },
  CONT: {
    min: 20000, max: 700000, recommended_min: 20000, recommended_max: 700000,
  },
  'CONT-8': {
    min: 20000, max: 700000, recommended_min: 20000, recommended_max: 700000,
  },
  'CONT-9': {
    min: 20000, max: 700000, recommended_min: 20000, recommended_max: 700000,
  },
  FABB: {
    min: 30000, max: 1500000, recommended_min: 30000, recommended_max: 1500000,
  },
  'FABB-9': {
    min: 30000, max: 1500000, recommended_min: 30000, recommended_max: 1500000,
  },
  'FABB-8': {
    min: 30000, max: 1500000, recommended_min: 30000, recommended_max: 1500000,
  },
  RILO: {
    min: 20000, max: 1500000, recommended_min: 20000, recommended_max: 1500000,
  },
  RTER: {
    min: 20000, max: 500000, recommended_min: 20000, recommended_max: 500000,
  },
  'RTER-8': {
    min: 20000, max: 500000, recommended_min: 20000, recommended_max: 500000,
  },
  'RTER-9': {
    min: 20000, max: 500000, recommended_min: 20000, recommended_max: 500000,
  },
  'REFR-8': {
    min: 2500, max: 25000, recommended_min: 2500, recommended_max: 25000,
  },
  'REFR-9': {
    min: 2500, max: 25000, recommended_min: 2500, recommended_max: 25000,
  },
  REFR: {
    min: 2500, max: 25000, recommended_min: 2500, recommended_max: 25000,
  },
  CRIS: {
    min: 1000, max: 5000, recommended_min: 1000, recommended_max: 5000,
  },
  ELE: {
    min: 2000, max: 20000, recommended_min: 2000, recommended_max: 20000,
  },
  INCI: {
    min: 0, max: 0, recommended_min: 0, recommended_max: 0,
  },
  RCT: {
    min: 0, max: 0, recommended_min: 0, recommended_max: 0,
  },
  PROP: {
    min: 0, max: 0, recommended_min: 0, recommended_max: 0,
  },
};

// ============================================================================
// 4. ASSET-SPECIFIC RULES
// ============================================================================

export const ASSET_RULES = {
  1: {
    property_type: '07', // flg_tipo_fabbricato
    location_name: 'ERCOLANO',
    location_code: 'H243',
    required_guarantees: ['CONT.F', 'RCT'],
    allowed_guarantees: ['CONT.F', 'CONT-8', 'CRIS', 'INCI', 'REFR-8', 'RILO', 'RCT', 'RTER-8'],
    capital_limits: {
      'CONT.F': { min: 5000, max: 150000 },
    },
  },
  2: {
    property_type: '01',
    location_name: 'PIOLTELLO',
    location_code: 'G686',
    required_guarantees: ['CONT-8', 'RCT'],
    allowed_guarantees: ['CONT-8', 'FABB-9', 'FABB-8', 'REFR-9', 'RTER-9', 'RILO', 'RCT'],
    capital_limits: {
      'CONT-8': { min: 10000, max: 100000 },
    },
  },
  3: {
    property_type: '01',
    location_name: 'PIOLTELLO',
    location_code: 'G686',
    required_guarantees: ['CONT-8', 'RCT'],
    allowed_guarantees: ['CONT-8', 'FABB-9', 'FABB-8', 'REFR-9', 'RTER-9', 'RILO', 'RCT'],
    capital_limits: {
      'CONT-8': { min: 10000, max: 100000 },
    },
  },
};

// ============================================================================
// 5. TECHNICAL DATA REQUIREMENTS
// ============================================================================

export const REQUIRED_TECHNICAL_DATA = [
  {
    cod_elenco_variabile: 'ANNOR', flg_formato_variabile: 'I', description: 'Anno di ristrutturazione', type: 'year',
  },
  {
    cod_elenco_variabile: 'TPAB', flg_formato_variabile: 'S', description: 'Tipo abitazione', required_value: '01',
  },
  {
    cod_elenco_variabile: 'ANNOC', flg_formato_variabile: 'I', description: 'Anno di costruzione', type: 'year',
  },
  { cod_elenco_variabile: 'ATTPR', flg_formato_variabile: 'F', description: 'Attività prevalente' },
  {
    cod_elenco_variabile: 'MTIMM', flg_formato_variabile: 'I', description: 'Metratura immobile', required_value: 95,
  },
];

export const REQUIRED_POLICY_VARIABLES = [
  { cod_elenco_variabile: 'DCONDSI', description: 'Condizioni sinistri' },
  { cod_elenco_variabile: 'DDESCRSI', description: 'Descrizione sinistri' },
  { cod_elenco_variabile: 'DSTUDSI', description: 'Studio sinistri' },
  { cod_elenco_variabile: 'DSIN5SI', description: 'Sinistri ultimi 5 anni' },
  { cod_elenco_variabile: 'DALTRSI', description: 'Altri sinistri' },
  { cod_elenco_variabile: 'AASI', description: 'Assicurazione attuale' },
  { cod_elenco_variabile: 'ATTECO', description: 'Codice Ateco', required: true },
];

// ============================================================================
// 6. CONTRACTUAL CONSTANTS
// ============================================================================

export const CONTRACT_CONSTANTS = {
  product_code: 95,
  product_name: 'COMMERCIO_PERSONA_FISICA',
  ramo: 504,
  valid_states: [1, 6, 12], // Proposal, Policy, Quote
  flg_durata: 2, // Annual
  flg_mezzo_incasso_polizza: 3,
  cod_frazionamento: '01', // Annual
  cod_valuta: 'EUR',
  cod_rete_vendita_agenzia: '3.568.0.0.0',
  cod_rete_vendita_produttore: '3.568.0.0.0',
  livello_rete_vendita_produttore: '1',
};

// ============================================================================
// 7. VALIDATION ERROR TYPES
// ============================================================================

export const ERROR_TYPES = {
  BLOCKING: 'BLOCKING', // Blocks submission
  WARNING: 'WARNING', // Shows warning but allows submission
  SUGGESTION: 'SUGGESTION', // Informational suggestions
};

export const ERROR_MESSAGES = {
  // Guarantee Compatibility
  INCOMPATIBLE_GUARANTEES: (guarantee, incompatible) => `Le garanzie: ${guarantee} incompatibili con: ${incompatible}`,
  MISSING_REQUIRED_GUARANTEE: 'Garanzia obbligatoria mancante',
  DUPLICATE_GUARANTEE: 'Garanzia duplicata non consentita',

  // Parameters
  INVALID_PARAMETER_VALUE: 'Valore parametro non valido',
  MISSING_REQUIRED_PARAMETER: 'Parametro obbligatorio mancante',
  INVALID_N_ADDETTI: 'Numero addetti deve essere tra 1 e 100',

  // Capitals
  CAPITAL_BELOW_MIN: 'Capitale assicurato inferiore al minimo',
  CAPITAL_ABOVE_MAX: 'Capitale assicurato superiore al massimo',
  CAPITAL_BELOW_RECOMMENDED: 'Capitale assicurato inferiore al valore raccomandato',
  CAPITAL_ABOVE_RECOMMENDED: 'Capitale assicurato superiore al valore raccomandato',

  // Assets
  ASSET_GUARANTEE_NOT_ALLOWED: 'Garanzia non consentita per questo asset',
  ASSET_MISSING_REQUIRED_GUARANTEE: 'Garanzia obbligatoria mancante per questo asset',
  INVALID_PROPERTY_TYPE: 'Tipo di proprietà non valido',

  // Temporal
  INVALID_EFFECTIVE_DATE: 'Data effetto deve essere >= oggi',
  INVALID_EXPIRY_DATE: 'Data scadenza deve essere data effetto + 1 anno',
  INVALID_INCLUSION_DATE: 'Data inclusione deve essere <= data effetto',
  INVALID_EXCLUSION_DATE: 'Data esclusione deve essere >= data inclusione',

  // Anagraphic
  INVALID_FISCAL_CODE: 'Codice fiscale non valido',
  INVALID_AGE: 'L\'età deve essere >= 18 anni',
  INVALID_GENDER: 'Sesso deve essere M o F',
  MISSING_MAIN_ADDRESS: 'Indirizzo principale mancante',
  INVALID_CAP: 'CAP deve essere di 5 cifre per Italia',
  MISSING_CONTRAENTE: 'Contraente predefinito mancante',
  MISSING_PROPRIETARIO: 'Proprietario predefinito mancante',

  // Technical Data
  MISSING_TECHNICAL_DATA: 'Dato tecnico obbligatorio mancante',
  INVALID_TECHNICAL_DATA_VALUE: 'Valore dato tecnico non valido',
  MISSING_POLICY_VARIABLE: 'Variabile polizza obbligatoria mancante',

  // Contractual
  INVALID_PRODUCT: 'Prodotto non valido',
  INVALID_CONTRACT_STATE: 'Stato contratto non valido',
  INVALID_PAYMENT_FREQUENCY: 'Frazionamento pagamento non valido',

  // Business Logic
  MISSING_PAYMENT_DATE: 'Data incasso obbligatoria quando incassa_polizza = S',
  MISSING_PAYMENT_TYPE: 'Tipo incasso obbligatorio quando incassa_polizza = S',
  INVALID_NETWORK: 'Rete di vendita non valida',
};

// ============================================================================
// 8. GUARANTEE CATEGORIES FOR UI
// ============================================================================

export const GUARANTEE_CATEGORIES = {
  FIRE: {
    label: 'Incendio',
    guarantees: ['CONT.F', 'CONT-8', 'FABB-9', 'FABB-8'],
    icon: '🔥',
  },
  THEFT: {
    label: 'Furto',
    guarantees: ['RILO', 'RTER-8', 'RTER-9'],
    icon: '🔒',
  },
  GLASS: {
    label: 'Cristalli',
    guarantees: ['REFR-8', 'REFR-9'],
    icon: '💎',
  },
  SPECIAL: {
    label: 'Speciali',
    guarantees: ['CRIS', 'INCI'],
    icon: '⚡',
  },
  MANDATORY: {
    label: 'Obbligatorie',
    guarantees: ['RCT'],
    icon: '⚠️',
  },
};

// ============================================================================
// 9. VALIDATION PRIORITY LEVELS
// ============================================================================

export const VALIDATION_PRIORITY = {
  LEVEL_1_BLOCKING: [
    'GARANZIE INCOMPATIBILI',
    'MISSING_REQUIRED_GUARANTEE',
    'INVALID_PARAMETER_VALUE',
    'MISSING_REQUIRED_PARAMETER',
    'CAPITAL_BELOW_MIN',
    'CAPITAL_ABOVE_MAX',
    'MISSING_TECHNICAL_DATA',
    'INVALID_FISCAL_CODE',
    'INVALID_AGE',
  ],
  LEVEL_2_WARNING: [
    'CAPITAL_BELOW_RECOMMENDED',
    'CAPITAL_ABOVE_RECOMMENDED',
    'INVALID_EFFECTIVE_DATE',
    'INVALID_EXPIRY_DATE',
  ],
  LEVEL_3_SUGGESTION: [
    'OPTIMIZE_GUARANTEE_COMBINATION',
    'BETTER_CAPITAL_ALLOCATION',
    'ADDITIONAL_OPTIONAL_GUARANTEES',
  ],
};
